import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { AuthLayout } from "../layouts/AuthLayout";
import { api, apiErrorMessage } from "../lib/api";
import { useAuthStore } from "../stores/auth-store";

const MIN_PASSWORD_LENGTH = 10;

interface InviteInfo {
  email: string | null;
  expiresAt: string;
}

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.bootstrap);

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .get<InviteInfo>(`/invitations/validate/${token}`)
      .then((r) => {
        setInvite(r.data);
        if (r.data.email) setEmail(r.data.email);
      })
      .catch((err) =>
        setLoadError(
          apiErrorMessage(err, "This invite link is invalid or has expired.")
        )
      );
  }, [token]);

  const passwordTooShort =
    password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (passwordTooShort || !token) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/accept-invite", {
        token,
        name,
        password,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // Only send email when the invite doesn't pre-fill it (open invite).
        ...(invite?.email ? {} : { email }),
      });
      // Bootstrap the session that was just created so the user lands on the dashboard.
      await setUser();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, "Could not create your account."));
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <AuthLayout title="Invite invalid" subtitle={loadError}>
        <Link
          to="/login"
          className="font-medium text-accent hover:underline text-sm"
        >
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (!invite) {
    return (
      <AuthLayout
        title="Checking invite…"
        subtitle="Validating your invite link"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent mx-auto" />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="You've been invited"
      subtitle="Create your account to get started"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Full name"
          name="name"
          autoComplete="name"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />

        {!invite.email && (
          <Input
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        )}

        {invite.email && (
          <Input
            label="Email"
            value={invite.email}
            disabled
            helperText="Set by your invite link."
          />
        )}

        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="new-password"
          placeholder="••••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={
            passwordTooShort
              ? `At least ${MIN_PASSWORD_LENGTH} characters.`
              : undefined
          }
          helperText={
            passwordTooShort
              ? undefined
              : `At least ${MIN_PASSWORD_LENGTH} characters.`
          }
          required
        />

        {error && (
          <p
            role="alert"
            className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        )}

        <Button type="submit" size="lg" loading={submitting} className="w-full">
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
